import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { View, Text, TouchableOpacity, ScrollView, TextInput, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Constants from 'expo-constants';
import { db } from '../firebaseConfig';
import { collection, query, orderBy, limit, getDocs, doc, updateDoc, where, getCountFromServer, startAfter } from 'firebase/firestore';
import { uiVar } from './floodlight/uiStyleCss';

const getBase = () => (
  (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
  || Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL
  || Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL
  || Constants?.manifest?.extra?.FUNCTIONS_BASE_URL
);

const FEEDBACK_COLLECTION = 'user_feedback';
const PAGE_SIZE = 25;

const TYPE_META = {
  query_accuracy: { label: 'Query Accuracy', color: '#8b5cf6', icon: 'search' },
  missing_results: { label: 'Missing Results', color: '#f59e0b', icon: 'alert-circle' },
  performance: { label: 'Performance', color: '#3b82f6', icon: 'speedometer' },
  feature_request: { label: 'Feature Request', color: '#10b981', icon: 'bulb' },
  bug: { label: 'Bug', color: '#ef4444', icon: 'bug' },
  general: { label: 'General', color: '#6b7280', icon: 'chatbubble-ellipses' },
};

const SENTIMENT_META = {
  positive: { color: '#10b981', icon: 'happy' },
  negative: { color: '#ef4444', icon: 'sad' },
  neutral: { color: '#6b7280', icon: 'remove-circle' },
};

const STATUS_OPTIONS = ['new', 'reviewed', 'actioned', 'dismissed'];
const STATUS_COLORS = { new: '#f59e0b', reviewed: '#3b82f6', actioned: '#10b981', dismissed: '#6b7280' };

export default function UserFeedbackTab() {
  const [feedbackDocs, setFeedbackDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSentiment, setFilterSentiment] = useState('all');
  const [searchText, setSearchText] = useState('');

  // AI Insights
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState(null);
  const [insightsExpanded, setInsightsExpanded] = useState(false);

  // Per-item editing
  const [editingId, setEditingId] = useState(null);
  const [editNotes, setEditNotes] = useState('');
  const [savingId, setSavingId] = useState(null);

  const loadFeedback = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const q = query(
        collection(db, FEEDBACK_COLLECTION),
        orderBy('createdAt', 'desc'),
        limit(200)
      );
      const snap = await getDocs(q);
      setFeedbackDocs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadFeedback(); }, [loadFeedback]);

  const filtered = useMemo(() => {
    let docs = feedbackDocs;
    if (filterType !== 'all') docs = docs.filter(d => d.feedbackType === filterType);
    if (filterStatus !== 'all') docs = docs.filter(d => d.status === filterStatus);
    if (filterSentiment !== 'all') docs = docs.filter(d => d.aiSentiment === filterSentiment);
    if (searchText.trim()) {
      const lower = searchText.toLowerCase();
      docs = docs.filter(d =>
        d.text?.toLowerCase().includes(lower) ||
        d.relatedQuery?.prompt?.toLowerCase().includes(lower) ||
        d.userEmail?.toLowerCase().includes(lower)
      );
    }
    return docs;
  }, [feedbackDocs, filterType, filterStatus, filterSentiment, searchText]);

  const stats = useMemo(() => {
    const total = feedbackDocs.length;
    const unreviewed = feedbackDocs.filter(d => d.status === 'new').length;
    const byType = {};
    const bySentiment = { positive: 0, negative: 0, neutral: 0, untagged: 0 };
    for (const d of feedbackDocs) {
      byType[d.feedbackType] = (byType[d.feedbackType] || 0) + 1;
      const s = d.aiSentiment || 'untagged';
      bySentiment[s] = (bySentiment[s] || 0) + 1;
    }
    return { total, unreviewed, byType, bySentiment };
  }, [feedbackDocs]);

  const generateInsights = useCallback(async () => {
    setInsightsLoading(true);
    setInsightsError(null);
    try {
      const base = getBase();
      const resp = await fetch(`${base}/getFeedbackInsights`);
      const json = await resp.json();
      if (!resp.ok) throw new Error(json.error || 'Failed to generate insights');
      setInsights(json);
      setInsightsExpanded(true);
    } catch (e) {
      setInsightsError(e.message);
    } finally {
      setInsightsLoading(false);
    }
  }, []);

  const handleStatusUpdate = useCallback(async (docId, status) => {
    setSavingId(docId);
    try {
      const base = getBase();
      await fetch(`${base}/updateFeedbackStatus`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ docId, status }),
      });
      setFeedbackDocs(prev => prev.map(d => d.id === docId ? { ...d, status } : d));
    } catch (e) {
      console.warn('[UserFeedbackTab] status update error:', e.message);
    } finally {
      setSavingId(null);
    }
  }, []);

  const handleSaveNotes = useCallback(async (docId) => {
    setSavingId(docId);
    try {
      const base = getBase();
      await fetch(`${base}/updateFeedbackStatus`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ docId, adminNotes: editNotes }),
      });
      setFeedbackDocs(prev => prev.map(d => d.id === docId ? { ...d, adminNotes: editNotes } : d));
      setEditingId(null);
    } catch (e) {
      console.warn('[UserFeedbackTab] notes save error:', e.message);
    } finally {
      setSavingId(null);
    }
  }, [editNotes]);

  const formatDate = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <View style={st.center}>
        <ActivityIndicator color="#a78bfa" />
        <Text style={st.loadingText}>Loading feedback…</Text>
      </View>
    );
  }

  return (
    <ScrollView style={st.wrap} contentContainerStyle={st.inner}>
      {/* Header */}
      <View style={st.headerRow}>
        <Text style={st.title}>User Feedback</Text>
        <TouchableOpacity style={st.refreshBtn} onPress={loadFeedback}>
          <Ionicons name="refresh" size={14} color="rgba(255,255,255,0.5)" />
          <Text style={st.refreshTxt}>Refresh</Text>
        </TouchableOpacity>
      </View>

      {error && (
        <View style={st.errorBox}>
          <Ionicons name="alert-circle" size={14} color="#ef4444" />
          <Text style={st.errorText}>{error}</Text>
        </View>
      )}

      {/* Stats bar */}
      <View style={st.statsRow}>
        {[
          { label: 'Total', value: stats.total, color: '#fff' },
          { label: 'New', value: stats.unreviewed, color: '#f59e0b' },
          { label: 'Positive', value: stats.bySentiment.positive, color: '#10b981' },
          { label: 'Negative', value: stats.bySentiment.negative, color: '#ef4444' },
          { label: 'Neutral', value: stats.bySentiment.neutral, color: '#6b7280' },
        ].map(({ label, value, color }) => (
          <View key={label} style={st.statCard}>
            <Text style={[st.statVal, { color }]}>{value}</Text>
            <Text style={st.statLbl}>{label}</Text>
          </View>
        ))}
      </View>

      {/* Type breakdown bar */}
      <View style={st.typeBreakdown}>
        {Object.entries(stats.byType).sort((a, b) => b[1] - a[1]).map(([type, count]) => {
          const meta = TYPE_META[type] || TYPE_META.general;
          return (
            <TouchableOpacity
              key={type}
              style={[st.typeBreakdownChip, filterType === type && { borderColor: meta.color + '80', backgroundColor: meta.color + '15' }]}
              onPress={() => setFilterType(filterType === type ? 'all' : type)}
            >
              <Ionicons name={meta.icon} size={11} color={meta.color} />
              <Text style={[st.typeBreakdownText, { color: meta.color }]}>{meta.label}</Text>
              <Text style={[st.typeBreakdownCount, { color: meta.color + 'aa' }]}>{count}</Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {/* AI Insights panel */}
      <View style={st.insightsPanel}>
        <View style={st.insightsPanelHeader}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7 }}>
            <Ionicons name="sparkles" size={14} color={uiVar('adm-accent', '#a78bfa')} />
            <Text style={st.insightsPanelTitle}>AI Insights</Text>
            {insights && (
              <Text style={st.insightsMeta}>
                {insights.count} entries · {insights.insights?.overallSentiment || ''}
              </Text>
            )}
          </View>
          <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
            {insights && (
              <TouchableOpacity onPress={() => setInsightsExpanded(e => !e)}>
                <Ionicons name={insightsExpanded ? 'chevron-up' : 'chevron-down'} size={14} color="rgba(255,255,255,0.4)" />
              </TouchableOpacity>
            )}
            <TouchableOpacity
              style={[st.insightsBtn, insightsLoading && { opacity: 0.6 }]}
              onPress={generateInsights}
              disabled={insightsLoading}
            >
              {insightsLoading
                ? <ActivityIndicator size="small" color="#a78bfa" />
                : <>
                    <Ionicons name="flash" size={12} color={uiVar('adm-accent', '#a78bfa')} />
                    <Text style={st.insightsBtnText}>{insights ? 'Regenerate' : 'Generate Insights'}</Text>
                  </>
              }
            </TouchableOpacity>
          </View>
        </View>

        {insightsError && (
          <Text style={{ color: '#ef4444', fontSize: 12, marginTop: 8 }}>{insightsError}</Text>
        )}

        {insights && insightsExpanded && (
          <View style={st.insightsBody}>
            {insights.insights?.executiveSummary && (
              <View style={st.execSummary}>
                <Text style={st.execSummaryText}>{insights.insights.executiveSummary}</Text>
              </View>
            )}

            {/* Sentiment breakdown */}
            <View style={st.sentimentRow}>
              {Object.entries(insights.sentimentBreakdown || {}).filter(([, v]) => v > 0).map(([s, v]) => {
                const meta = SENTIMENT_META[s] || { color: '#6b7280', icon: 'ellipse' };
                return (
                  <View key={s} style={st.sentimentChip}>
                    <Ionicons name={meta.icon} size={12} color={meta.color} />
                    <Text style={[st.sentimentLabel, { color: meta.color }]}>{v} {s}</Text>
                  </View>
                );
              })}
            </View>

            {/* Themes */}
            {insights.insights?.themes?.length > 0 && (
              <View style={st.insightsSection}>
                <Text style={st.insightsSectionTitle}>Top Themes</Text>
                {insights.insights.themes.map((t, i) => {
                  const severityColor = t.severity === 'critical' ? '#ef4444' : t.severity === 'high' ? '#f59e0b' : t.severity === 'medium' ? '#3b82f6' : '#6b7280';
                  return (
                    <View key={i} style={st.themeCard}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                        <Text style={st.themeTitle}>{t.title}</Text>
                        <View style={{ flexDirection: 'row', gap: 6, alignItems: 'center' }}>
                          {t.count && <Text style={{ color: 'rgba(255,255,255,0.3)', fontSize: 10 }}>{t.count}x</Text>}
                          <View style={[st.severityBadge, { backgroundColor: severityColor + '20' }]}>
                            <Text style={[st.severityText, { color: severityColor }]}>{t.severity}</Text>
                          </View>
                        </View>
                      </View>
                      <Text style={st.themeDesc}>{t.description}</Text>
                    </View>
                  );
                })}
              </View>
            )}

            {/* Recommendations */}
            {insights.insights?.recommendations?.length > 0 && (
              <View style={st.insightsSection}>
                <Text style={st.insightsSectionTitle}>Recommendations</Text>
                {insights.insights.recommendations.map((r, i) => {
                  const pColor = r.priority === 'P1' ? '#ef4444' : r.priority === 'P2' ? '#f59e0b' : '#6b7280';
                  return (
                    <View key={i} style={st.recCard}>
                      <View style={[st.recPriority, { backgroundColor: pColor + '20' }]}>
                        <Text style={[st.recPriorityText, { color: pColor }]}>{r.priority}</Text>
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={st.recAction}>{r.action}</Text>
                        <Text style={st.recRationale}>{r.rationale}</Text>
                      </View>
                    </View>
                  );
                })}
              </View>
            )}

            {/* Top queries mentioned */}
            {insights.insights?.topQueries?.length > 0 && (
              <View style={st.insightsSection}>
                <Text style={st.insightsSectionTitle}>Frequently Mentioned</Text>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                  {insights.insights.topQueries.map((q, i) => (
                    <View key={i} style={st.topQueryChip}>
                      <Text style={st.topQueryText}>{q}</Text>
                    </View>
                  ))}
                </View>
              </View>
            )}
          </View>
        )}
      </View>

      {/* Filters */}
      <View style={st.filtersRow}>
        <TextInput
          style={st.searchInput}
          placeholder="Search feedback…"
          placeholderTextColor="rgba(255,255,255,0.25)"
          value={searchText}
          onChangeText={setSearchText}
        />
        <View style={st.filterGroup}>
          {['all', 'new', 'reviewed', 'actioned', 'dismissed'].map(s => (
            <TouchableOpacity
              key={s}
              style={[st.filterChip, filterStatus === s && { backgroundColor: (STATUS_COLORS[s] || '#a78bfa') + '25', borderColor: (STATUS_COLORS[s] || '#a78bfa') + '60' }]}
              onPress={() => setFilterStatus(s)}
            >
              <Text style={[st.filterChipText, filterStatus === s && { color: STATUS_COLORS[s] || uiVar('adm-accent', '#a78bfa') }]}>
                {s === 'all' ? 'All statuses' : s}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
        <View style={st.filterGroup}>
          {['all', 'positive', 'negative', 'neutral'].map(s => (
            <TouchableOpacity
              key={s}
              style={[st.filterChip, filterSentiment === s && { backgroundColor: (SENTIMENT_META[s]?.color || '#a78bfa') + '25', borderColor: (SENTIMENT_META[s]?.color || '#a78bfa') + '60' }]}
              onPress={() => setFilterSentiment(s)}
            >
              <Text style={[st.filterChipText, filterSentiment === s && { color: SENTIMENT_META[s]?.color || uiVar('adm-accent', '#a78bfa') }]}>
                {s === 'all' ? 'All sentiment' : s}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      <Text style={st.countLine}>{filtered.length} of {feedbackDocs.length} entries</Text>

      {/* Feedback list */}
      {filtered.length === 0 ? (
        <View style={st.emptyBox}>
          <Ionicons name="chatbubbles-outline" size={36} color="rgba(255,255,255,0.15)" />
          <Text style={st.emptyText}>No feedback matches these filters.</Text>
        </View>
      ) : (
        filtered.map(item => <FeedbackCard
          key={item.id}
          item={item}
          editing={editingId === item.id}
          editNotes={editNotes}
          setEditNotes={setEditNotes}
          saving={savingId === item.id}
          onStartEdit={() => { setEditingId(item.id); setEditNotes(item.adminNotes || ''); }}
          onCancelEdit={() => setEditingId(null)}
          onSaveNotes={() => handleSaveNotes(item.id)}
          onStatusChange={(s) => handleStatusUpdate(item.id, s)}
          formatDate={formatDate}
        />)
      )}
    </ScrollView>
  );
}

const HISTORY_PAGE_SIZE = 25;

// ─── User History Drill-Down ──────────────────────────────────────────────────
function UserHistoryDrillDown({ uid, formatDate }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadedOnce, setLoadedOnce] = useState(false);

  const loadPage = useCallback(async (cursor = null) => {
    if (!uid) return;
    setLoading(true);
    try {
      let q = query(
        collection(db, 'users', uid, 'query_history'),
        orderBy('createdAt', 'desc'),
        limit(HISTORY_PAGE_SIZE)
      );
      if (cursor) q = query(
        collection(db, 'users', uid, 'query_history'),
        orderBy('createdAt', 'desc'),
        startAfter(cursor),
        limit(HISTORY_PAGE_SIZE)
      );
      const snap = await getDocs(q);
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setEntries(prev => cursor ? [...prev, ...docs] : docs);
      setLastDoc(snap.docs[snap.docs.length - 1] || null);
      setHasMore(docs.length === HISTORY_PAGE_SIZE);
      setLoadedOnce(true);
    } catch (e) {
      console.error('[UserHistory] load error', e);
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => { if (!loadedOnce) loadPage(); }, [loadPage, loadedOnce]);

  const statusColor = (s) => s === 'completed' ? '#10b981' : s === 'error' ? '#ef4444' : s === 'processing' ? '#3b82f6' : '#6b7280';

  if (loading && !loadedOnce) {
    return (
      <View style={{ paddingVertical: 16, alignItems: 'center' }}>
        <ActivityIndicator size="small" color="#a78bfa" />
      </View>
    );
  }

  if (entries.length === 0) {
    return <Text style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, paddingVertical: 12, textAlign: 'center' }}>No query history found for this user.</Text>;
  }

  return (
    <View>
      {entries.map((e, idx) => (
        <View key={e.id || idx} style={st.historyEntry}>
          <View style={{ flex: 1 }}>
            <Text style={st.historyPrompt} numberOfLines={2}>{e.prompt || '—'}</Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 3 }}>
              <View style={[st.historyStatusChip, { backgroundColor: statusColor(e.status) + '20' }]}>
                <Text style={[st.historyStatusText, { color: statusColor(e.status) }]}>{e.status}</Text>
              </View>
              {e.resultsCount != null && (
                <Text style={st.historyMeta}>{e.resultsCount} results</Text>
              )}
              {e.durationMs != null && (
                <Text style={st.historyMeta}>{(e.durationMs / 1000).toFixed(1)}s</Text>
              )}
            </View>
          </View>
          <Text style={st.historyDate}>{formatDate(e.createdAt)}</Text>
        </View>
      ))}
      {hasMore && (
        <TouchableOpacity
          style={st.loadMoreBtn}
          onPress={() => loadPage(lastDoc)}
          disabled={loading}
        >
          {loading
            ? <ActivityIndicator size="small" color="#a78bfa" />
            : <>
                <Ionicons name="chevron-down" size={12} color={uiVar('adm-accent', '#a78bfa')} />
                <Text style={st.loadMoreText}>Load more</Text>
              </>
          }
        </TouchableOpacity>
      )}
    </View>
  );
}

// ─── Feedback Card ────────────────────────────────────────────────────────────
function FeedbackCard({ item, editing, editNotes, setEditNotes, saving, onStartEdit, onCancelEdit, onSaveNotes, onStatusChange, formatDate }) {
  const [historyOpen, setHistoryOpen] = useState(false);
  const typeMeta = TYPE_META[item.feedbackType] || TYPE_META.general;
  const sentMeta = SENTIMENT_META[item.aiSentiment] || null;
  const statusColor = STATUS_COLORS[item.status] || '#6b7280';

  return (
    <View style={st.card}>
      {/* Card header */}
      <View style={st.cardHeader}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7, flex: 1, flexWrap: 'wrap' }}>
          {/* Type badge */}
          <View style={[st.badge, { backgroundColor: typeMeta.color + '20', borderColor: typeMeta.color + '40' }]}>
            <Ionicons name={typeMeta.icon} size={10} color={typeMeta.color} />
            <Text style={[st.badgeText, { color: typeMeta.color }]}>{typeMeta.label}</Text>
          </View>
          {/* Sentiment badge */}
          {sentMeta && (
            <View style={[st.badge, { backgroundColor: sentMeta.color + '20', borderColor: sentMeta.color + '40' }]}>
              <Ionicons name={sentMeta.icon} size={10} color={sentMeta.color} />
              <Text style={[st.badgeText, { color: sentMeta.color }]}>{item.aiSentiment}</Text>
            </View>
          )}
          {/* Priority */}
          {item.aiPriority && (
            <View style={[st.badge, { backgroundColor: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)' }]}>
              <Text style={[st.badgeText, { color: 'rgba(255,255,255,0.4)' }]}>{item.aiPriority} priority</Text>
            </View>
          )}
          {/* Status */}
          <View style={[st.badge, { backgroundColor: statusColor + '20', borderColor: statusColor + '40' }]}>
            <View style={[st.dot, { backgroundColor: statusColor }]} />
            <Text style={[st.badgeText, { color: statusColor }]}>{item.status}</Text>
          </View>
        </View>
        <Text style={st.cardDate}>{formatDate(item.createdAt)}</Text>
      </View>

      {/* User + version info */}
      <View style={st.metaRow}>
        <Ionicons name="person-circle-outline" size={12} color="rgba(255,255,255,0.3)" />
        <Text style={st.metaText}>{item.userEmail || item.uid?.slice(0, 12) || 'Anonymous'}</Text>
        {item.appVersion && (
          <>
            <Text style={st.metaDot}>·</Text>
            <Text style={st.metaText}>v{item.appVersion}</Text>
          </>
        )}
        {item.userTier && (
          <>
            <Text style={st.metaDot}>·</Text>
            <Text style={[st.metaText, { color: uiVar('adm-accent', '#a78bfa') }]}>{item.userTier}</Text>
          </>
        )}
      </View>

      {/* Linked query */}
      {item.relatedQuery?.prompt && (
        <View style={st.linkedQuery}>
          <Ionicons name="search" size={11} color="#3b82f6" />
          <Text style={st.linkedQueryText} numberOfLines={1}>
            Re: "{item.relatedQuery.prompt}"
          </Text>
          {item.relatedQuery.resultsCount != null && (
            <Text style={st.linkedQueryCount}>{item.relatedQuery.resultsCount} results</Text>
          )}
        </View>
      )}

      {/* Feedback text */}
      <Text style={st.feedbackText}>{item.text}</Text>

      {/* AI tags */}
      {item.aiTags?.length > 0 && (
        <View style={st.aiTagsRow}>
          <Ionicons name="sparkles" size={10} color={uiVar('adm-accent', '#a78bfa')} />
          {item.aiTags.map((tag, i) => (
            <View key={i} style={st.aiTag}>
              <Text style={st.aiTagText}>{tag}</Text>
            </View>
          ))}
        </View>
      )}

      {/* AI summary */}
      {item.aiSummary && (
        <Text style={st.aiSummary}>"{item.aiSummary}"</Text>
      )}

      {/* Admin notes */}
      {editing ? (
        <View style={st.notesEdit}>
          <TextInput
            style={st.notesInput}
            value={editNotes}
            onChangeText={setEditNotes}
            placeholder="Admin notes…"
            placeholderTextColor="rgba(255,255,255,0.2)"
            multiline
          />
          <View style={{ flexDirection: 'row', gap: 8, marginTop: 6 }}>
            <TouchableOpacity style={st.saveNotesBtn} onPress={onSaveNotes} disabled={saving}>
              {saving ? <ActivityIndicator size="small" color="#10b981" /> : <Text style={{ color: '#10b981', fontSize: 12, fontWeight: '600' }}>Save</Text>}
            </TouchableOpacity>
            <TouchableOpacity onPress={onCancelEdit}>
              <Text style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12 }}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      ) : item.adminNotes ? (
        <TouchableOpacity style={st.notesBubble} onPress={onStartEdit}>
          <Ionicons name="create-outline" size={11} color="#3b82f6" />
          <Text style={st.notesBubbleText}>{item.adminNotes}</Text>
        </TouchableOpacity>
      ) : null}

      {/* User History drill-down */}
      {item.uid && (
        <View style={st.historySection}>
          <TouchableOpacity
            style={st.historyToggle}
            onPress={() => setHistoryOpen(p => !p)}
            activeOpacity={0.7}
          >
            <Ionicons name="time-outline" size={12} color="rgba(255,255,255,0.4)" />
            <Text style={st.historyToggleText}>User Query History</Text>
            <Ionicons name={historyOpen ? 'chevron-up' : 'chevron-down'} size={12} color="rgba(255,255,255,0.3)" />
          </TouchableOpacity>
          {historyOpen && (
            <View style={st.historyPanel}>
              <UserHistoryDrillDown uid={item.uid} formatDate={formatDate} />
            </View>
          )}
        </View>
      )}

      {/* Actions */}
      <View style={st.actionsRow}>
        <View style={{ flexDirection: 'row', gap: 5, flexWrap: 'wrap' }}>
          {STATUS_OPTIONS.filter(s => s !== item.status).map(s => (
            <TouchableOpacity
              key={s}
              style={[st.actionBtn, { borderColor: (STATUS_COLORS[s] || '#6b7280') + '50' }]}
              onPress={() => onStatusChange(s)}
              disabled={saving}
            >
              <Text style={[st.actionBtnText, { color: STATUS_COLORS[s] || uiVar('adm-muted2', '#6b7280') }]}>Mark {s}</Text>
            </TouchableOpacity>
          ))}
        </View>
        {!editing && (
          <TouchableOpacity style={st.notesBtn} onPress={onStartEdit}>
            <Ionicons name="create-outline" size={12} color="rgba(255,255,255,0.35)" />
            <Text style={st.notesBtnText}>Notes</Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const st = {
  wrap: { flex: 1, backgroundColor: 'transparent' },
  inner: { padding: 20, paddingBottom: 60 },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', gap: 12, minHeight: 200 },
  loadingText: { color: 'rgba(255,255,255,0.4)', fontSize: 13 },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  title: { color: uiVar('adm-ink', '#fff'), fontSize: 20, fontWeight: '700' },
  refreshBtn: { flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: 8, paddingHorizontal: 10, paddingVertical: 6 },
  refreshTxt: { color: 'rgba(255,255,255,0.4)', fontSize: 12 },
  errorBox: { flexDirection: 'row', alignItems: 'center', gap: 7, backgroundColor: 'rgba(239,68,68,0.1)', borderRadius: 8, padding: 10, marginBottom: 12 },
  errorText: { color: '#ef4444', fontSize: 12, flex: 1 },

  statsRow: { flexDirection: 'row', gap: 8, marginBottom: 12, flexWrap: 'wrap' },
  statCard: { backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: 10, borderWidth: 1, borderColor: 'rgba(255,255,255,0.07)', padding: 12, minWidth: 70, alignItems: 'center' },
  statVal: { fontSize: 22, fontWeight: '700' },
  statLbl: { color: 'rgba(255,255,255,0.4)', fontSize: 10, fontWeight: '500', marginTop: 2, textTransform: 'uppercase' },

  typeBreakdown: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 16 },
  typeBreakdownChip: { flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: 20, borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)', paddingHorizontal: 10, paddingVertical: 5 },
  typeBreakdownText: { fontSize: 11, fontWeight: '500' },
  typeBreakdownCount: { fontSize: 10, fontWeight: '700' },

  insightsPanel: { backgroundColor: uiVar('adm-accent-wash-6', 'rgba(167,139,250,0.06)'), borderRadius: 12, borderWidth: 1, borderColor: uiVar('adm-accent-wash-20', 'rgba(167,139,250,0.2)'), padding: 14, marginBottom: 18 },
  insightsPanelHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  insightsPanelTitle: { color: uiVar('adm-ink', '#fff'), fontSize: 14, fontWeight: '700' },
  insightsMeta: { color: 'rgba(255,255,255,0.35)', fontSize: 11, marginLeft: 4 },
  insightsBtn: { flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: uiVar('adm-accent-wash-15', 'rgba(167,139,250,0.15)'), borderRadius: 8, paddingHorizontal: 10, paddingVertical: 6, borderWidth: 1, borderColor: uiVar('adm-accent-wash-30', 'rgba(167,139,250,0.3)') },
  insightsBtnText: { color: uiVar('adm-accent', '#a78bfa'), fontSize: 12, fontWeight: '600' },
  insightsBody: { marginTop: 14, gap: 12 },
  execSummary: { backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: 12 },
  execSummaryText: { color: 'rgba(255,255,255,0.75)', fontSize: 13, lineHeight: 19 },
  sentimentRow: { flexDirection: 'row', gap: 8, flexWrap: 'wrap' },
  sentimentChip: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  sentimentLabel: { fontSize: 12, fontWeight: '500' },
  insightsSection: { gap: 8 },
  insightsSectionTitle: { color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5 },
  themeCard: { backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: 10 },
  themeTitle: { color: uiVar('adm-ink', '#fff'), fontSize: 13, fontWeight: '600' },
  themeDesc: { color: 'rgba(255,255,255,0.5)', fontSize: 12, marginTop: 2 },
  severityBadge: { borderRadius: 4, paddingHorizontal: 6, paddingVertical: 2 },
  severityText: { fontSize: 9, fontWeight: '700', textTransform: 'uppercase' },
  recCard: { flexDirection: 'row', gap: 10, backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: 10, alignItems: 'flex-start' },
  recPriority: { borderRadius: 6, paddingHorizontal: 8, paddingVertical: 3, alignSelf: 'flex-start' },
  recPriorityText: { fontSize: 10, fontWeight: '800', letterSpacing: 0.3 },
  recAction: { color: uiVar('adm-ink', '#fff'), fontSize: 12, fontWeight: '600' },
  recRationale: { color: 'rgba(255,255,255,0.45)', fontSize: 11, marginTop: 2 },
  topQueryChip: { backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: 6, paddingHorizontal: 9, paddingVertical: 4 },
  topQueryText: { color: 'rgba(255,255,255,0.55)', fontSize: 11 },

  filtersRow: { gap: 10, marginBottom: 12 },
  searchInput: { backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 8, borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)', color: uiVar('adm-ink', '#fff'), fontSize: 13, paddingHorizontal: 12, paddingVertical: 8, outlineStyle: 'none' },
  filterGroup: { flexDirection: 'row', flexWrap: 'wrap', gap: 5 },
  filterChip: { borderRadius: 20, borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)', paddingHorizontal: 10, paddingVertical: 5 },
  filterChipText: { color: 'rgba(255,255,255,0.4)', fontSize: 11, fontWeight: '500' },

  countLine: { color: 'rgba(255,255,255,0.3)', fontSize: 11, marginBottom: 12 },

  emptyBox: { alignItems: 'center', paddingVertical: 48, gap: 10 },
  emptyText: { color: 'rgba(255,255,255,0.3)', fontSize: 14 },

  card: { backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: 12, borderWidth: 1, borderColor: 'rgba(255,255,255,0.07)', padding: 14, marginBottom: 10 },
  cardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, gap: 8 },
  cardDate: { color: 'rgba(255,255,255,0.25)', fontSize: 10, flexShrink: 0 },
  badge: { flexDirection: 'row', alignItems: 'center', gap: 4, borderRadius: 10, borderWidth: 1, paddingHorizontal: 7, paddingVertical: 3 },
  badgeText: { fontSize: 10, fontWeight: '600' },
  dot: { width: 5, height: 5, borderRadius: 3 },
  metaRow: { flexDirection: 'row', alignItems: 'center', gap: 5, marginBottom: 7 },
  metaText: { color: 'rgba(255,255,255,0.3)', fontSize: 11 },
  metaDot: { color: 'rgba(255,255,255,0.15)', fontSize: 11 },
  linkedQuery: { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: 'rgba(59,130,246,0.08)', borderRadius: 6, paddingHorizontal: 9, paddingVertical: 5, marginBottom: 8 },
  linkedQueryText: { color: '#3b82f6', fontSize: 11, flex: 1 },
  linkedQueryCount: { color: 'rgba(59,130,246,0.5)', fontSize: 10 },
  feedbackText: { color: 'rgba(255,255,255,0.75)', fontSize: 13, lineHeight: 19, marginBottom: 8 },
  aiTagsRow: { flexDirection: 'row', alignItems: 'center', gap: 5, flexWrap: 'wrap', marginBottom: 5 },
  aiTag: { backgroundColor: uiVar('adm-accent-wash-12', 'rgba(167,139,250,0.12)'), borderRadius: 4, paddingHorizontal: 6, paddingVertical: 2 },
  aiTagText: { color: uiVar('adm-accent', '#a78bfa'), fontSize: 10, fontWeight: '500' },
  aiSummary: { color: 'rgba(255,255,255,0.35)', fontSize: 11, fontStyle: 'italic', marginBottom: 8 },
  actionsRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, paddingTop: 8, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.05)' },
  actionBtn: { borderRadius: 6, borderWidth: 1, paddingHorizontal: 9, paddingVertical: 4 },
  actionBtnText: { fontSize: 11, fontWeight: '500' },
  notesBtn: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  notesBtnText: { color: 'rgba(255,255,255,0.3)', fontSize: 11 },
  notesEdit: { backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: 10, marginTop: 6 },
  notesInput: { color: uiVar('adm-ink', '#fff'), fontSize: 12, minHeight: 50, outlineStyle: 'none' },
  saveNotesBtn: { backgroundColor: 'rgba(16,185,129,0.15)', borderRadius: 6, paddingHorizontal: 12, paddingVertical: 5 },
  notesBubble: { flexDirection: 'row', alignItems: 'flex-start', gap: 5, backgroundColor: 'rgba(59,130,246,0.08)', borderRadius: 6, padding: 8, marginTop: 6 },
  notesBubbleText: { color: 'rgba(255,255,255,0.5)', fontSize: 11, flex: 1 },

  historySection: { marginTop: 8, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.05)', paddingTop: 8 },
  historyToggle: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  historyToggleText: { color: 'rgba(255,255,255,0.35)', fontSize: 11, fontWeight: '600', flex: 1 },
  historyPanel: { marginTop: 8, backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: 8, padding: 10 },
  historyEntry: { flexDirection: 'row', alignItems: 'flex-start', gap: 10, paddingVertical: 7, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.04)' },
  historyPrompt: { color: 'rgba(255,255,255,0.65)', fontSize: 12, lineHeight: 17 },
  historyStatusChip: { borderRadius: 4, paddingHorizontal: 6, paddingVertical: 2 },
  historyStatusText: { fontSize: 9, fontWeight: '700', textTransform: 'uppercase' },
  historyMeta: { color: 'rgba(255,255,255,0.25)', fontSize: 10 },
  historyDate: { color: 'rgba(255,255,255,0.2)', fontSize: 10, flexShrink: 0, marginTop: 2 },
  loadMoreBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 5, paddingVertical: 8, marginTop: 4 },
  loadMoreText: { color: uiVar('adm-accent', '#a78bfa'), fontSize: 12, fontWeight: '600' },
};
